import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AnalyticsApi from 'libs/api/analytics';
import useFilters from '../useFilters';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import { Table } from 'antd';
import Text from 'antd/es/typography/Text';
import { fileNameEdit } from 'helpers/FileUtils';
import { usePdf } from '../../../../hooks/usePdf';

const api = new AnalyticsApi('charts/brand_cross_village/');

const format = (v) => `${v}%`;

const defaultState = {
  tableData: [],
  lineChartData2: [],
  lineChartData: [],
  total: {},
};

const useInvoicesTickets = (isPrint, allReportsData, isAllReports) => {
  const { t } = useTranslation();
  const { filters, title, searchParams } = useFilters(
    false,
    false,
    isAllReports,
  );
  const { handleDownloadPdf, loadingPdf, createCustomEvent } = usePdf();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultState);
  const [errors, setErrors] = useState([]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const summary = useCallback(() => {
    const { total = {} } = data;

    delete total.Periodo;

    const tableKeys = Object.keys(total);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text
                strong
                style={{
                  minWidth: 50,
                }}
              >
                {render(total[dataIndex])}
              </Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data]);

  const [columns, setColumns] = useState([
    {
      title: t('Periodo'),
      dataIndex: 'Periodo',
    },
    {
      title: t('Sales Store TOV'),
      dataIndex: 'Sales Store TOV',
      render: (v) => Helpers.numberWithCommas(v, ' €'),
    },
    {
      title: t('Sales TOV vs LY (+/-%)'),
      dataIndex: 'Sales TOV vs LY (+/-%)',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Sales Store SOV'),
      dataIndex: 'Sales Store SOV',
      render: (v) => Helpers.numberWithCommas(v, ' €'),
    },
    {
      title: t('Sales SOV vs LY (+/-%)'),
      dataIndex: 'Sales SOV vs LY (+/-%)',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },

    {
      title: t(''),
      dataIndex: 'Sales SOV vs LY (+/-%)',
      width: 50,
      render: () => '',
      format: () => '',
    },
    {
      title: t('CR TOV'),
      dataIndex: 'CR TOV',
      render: (v) => Helpers.numberWithCommas(v),
    },
    {
      title: t('CR SOV'),
      dataIndex: 'CR SOV',
      render: (v) => Helpers.numberWithCommas(v),
    },
    {
      title: t('ATV TOV'),
      dataIndex: 'ATV TOV',
      render: (v) => Helpers.numberWithCommas(v, ' €'),
    },
    {
      title: t('ATV SOV'),
      dataIndex: 'ATV SOV',
      render: (v) => Helpers.numberWithCommas(v, ' €'),
    },
    {
      title: t('UPT TOV'),
      dataIndex: 'UPT TOV',
      render: (v) => Helpers.numberWithCommas(v),
    },
    {
      title: t('UPT SOV'),
      dataIndex: 'UPT SOV',
      render: (v) => Helpers.numberWithCommas(v),
    },
  ]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (
          !params.by_period ||
          !params.by_shopping_center ||
          !params.by_company
        )
          return;

        let data, total, error;

        if (!isAllReports) {
          const {
            data: { data: backData, total: backTotal, error: backError = '' },
          } = await api.getFootfalls({ params });
          data = backData;
          total = backTotal;
          error = backError;
        } else {
          const {
            data: backData,
            total: backTotal,
            error: backError = '',
          } = allReportsData;
          data = backData;
          total = backTotal;
          error = backError;
        }

        if (!data) {
          return;
        }

        const lineChartData1 = {
          id: 'Sales Store TOV',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Sales Store TOV'],
          })),
        };
        const lineChartData2 = {
          id: 'Sales Store SOV',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Sales Store SOV'],
          })),
        };

        setData({
          tableData: data,
          lineChartData: [lineChartData1, lineChartData2],
          total,
          error,
        });
        setErrors([]);
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setData(defaultState);
        setErrors(UIHelper.showAnalyticsError(e));
      } finally {
        setLoading(false);
        if (isPrint) {
          createCustomEvent();
        }
      }
    })();
  }, [searchParams, isPrint, allReportsData, isAllReports]);

  return {
    columns,
    data,
    filters,
    format,
    summary,
    loading,
    handleDownloadExcel,
    title,
    searchParams,
    handleDownloadPdf,
    loadingPdf,
    errors,
  };
};

export default useInvoicesTickets;
